import React from 'react'
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { ChartInfoButton } from '@/components/shared/ChartInfoButton'

export interface RevenueForecastPoint {
  month: string
  actual: number | null
  projected: number | null
}

interface Props {
  data: RevenueForecastPoint[] | undefined
  loading?: boolean
  error?: unknown
}

function euro(value: number | null | undefined): string {
  if (value == null) return '—'
  if (value >= 1_000_000_000) return `€${(value / 1_000_000_000).toFixed(2)}B`
  if (value >= 1_000_000) return `€${(value / 1_000_000).toFixed(1)}M`
  if (value >= 1_000) return `€${(value / 1_000).toFixed(0)}k`
  return `€${Math.round(value)}`
}

export function ForecastChart({ data, loading, error }: Props) {
  if (error) {
    return <div className="card text-sm text-red-600">Failed to load revenue forecast.</div>
  }
  return (
    <div className="card">
      <div className="flex items-start justify-between mb-3">
        <div>
          <h2 className="text-base font-semibold text-secondary-900">Revenue forecast</h2>
          <p className="text-xs text-secondary-500">
            Trailing 6 months actual GMV · projection = trailing-90d avg monthly GMV
          </p>
        </div>
        <ChartInfoButton
          title="Revenue forecast"
          info={{
            definition:
              'Monthly closed-sales GMV (trailing 6 months) projected forward using the trailing-90-day sales pace. Order-of-magnitude forecast, not a commitment.',
            formula:
              'actual = SUM(current_price) WHERE status = sold grouped by month(sales_date). projected = avg monthly GMV of the last 3 months, extended forward.',
            calculation:
              'Projection line is drawn flat at the trailing-90d avg — it does not attempt seasonality or ramp. Deliberately simple so the number is auditable. Actual line uses the sale price recorded at sales_date.',
            source: 'unit.current_price, unit.sales_date, unit.status',
          }}
        />
      </div>
      {loading || !data ? (
        <div className="h-[320px] animate-pulse bg-secondary-100 rounded" />
      ) : data.length === 0 ? (
        <div className="text-sm text-secondary-500">No data for current filters.</div>
      ) : (
        <ResponsiveContainer width="100%" height={320}>
          <LineChart data={data} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis dataKey="month" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => euro(v)} />
            <Tooltip formatter={(value: number) => [euro(value), '']} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Line
              type="monotone"
              dataKey="actual"
              name="Actual"
              stroke="#16a34a"
              strokeWidth={2}
              dot={{ r: 3 }}
              connectNulls
            />
            <Line
              type="monotone"
              dataKey="projected"
              name="Projected"
              stroke="#2563eb"
              strokeWidth={2}
              strokeDasharray="6 4"
              dot={false}
              connectNulls
            />
          </LineChart>
        </ResponsiveContainer>
      )}
    </div>
  )
}
